"use client";

import { useState, useRef } from "react";
import Link from "next/link";
import Image from "next/image";
import { ArrowRight, Lightbulb } from "lucide-react";
import { motion } from "framer-motion";
import { trackBlogRead } from "@/utils/gtm";
import blogPowerBiTableau from "@/data/blogs/power-bi-vs-tableau-2025.json";
import blogTopItJobs from "@/data/blogs/top-10-it-jobs-in-demand-2025.json";
import blogBreakIntoUs from "@/data/blogs/break-into-us-it-job-market.json";
import blogSalesforce from "@/data/blogs/salesforce-career-guide-2025.json";

const BLOG_IMAGES: Record<string, string> = {
    "power-bi-vs-tableau-2025": "/images/skill-1.jpg",
    "top-10-it-jobs-in-demand-2025": "/images/skill-4.jpg",
    "break-into-us-it-job-market": "/images/skill-2.jpg",
    "salesforce-career-guide-2025": "/images/skill-3.jpg",
};

const featuredBlogs = [blogPowerBiTableau, blogTopItJobs, blogBreakIntoUs, blogSalesforce];

function getExcerpt(content: string) {
    const plain = content.replace(/#{1,6}\s/g, "").replace(/\*\*/g, "");
    return plain.substring(0, 120) + "...";
}

export function BlogLinks() {
    const [activeIndex, setActiveIndex] = useState(0);
    const scrollRef = useRef<HTMLDivElement>(null);

    const scrollTo = (index: number) => {
        setActiveIndex(index);
        if (scrollRef.current) {
            const scrollTarget = index * (scrollRef.current.scrollWidth / featuredBlogs.length);
            scrollRef.current.scrollTo({
                left: scrollTarget,
                behavior: "smooth"
            });
        }
    };

    return (
        <section className="py-24 bg-white overflow-hidden">
            <div className="max-w-[1440px] mx-auto px-4 md:px-8 lg:px-12">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-16"
                >
                    <div className="flex items-center justify-center gap-2 mb-4">
                        <div className="w-10 h-10 bg-[#F0F4FF] rounded-lg flex items-center justify-center">
                            <Lightbulb className="text-[#1B61FF] w-5 h-5" />
                        </div>
                        <span className="text-[#1B61FF] font-semibold text-sm">Blog & News</span>
                    </div>
                    <h2 className="text-[#1A1A1A] text-3xl md:text-4xl font-bold mb-6">
                        Smart Learning Blog
                    </h2>
                    <p className="text-[#666666] max-w-2xl mx-auto text-[18px] leading-relaxed">
                        Stay updated with the latest trends in technology, professional development, and educational innovations.
                    </p>
                </motion.div>

                {/* Blog Cards */}
                <div
                    ref={scrollRef}
                    className="flex gap-6 overflow-x-auto no-scrollbar scroll-smooth mb-8 pb-4 snap-x snap-mandatory"
                >
                    {featuredBlogs.map((post) => (
                        <Link
                            key={post.slug}
                            href={`/blog/${post.slug}`}
                            onClick={() => trackBlogRead(post.hero.title, post.hero.category)}
                            className="min-w-[100%] sm:min-w-[calc(50%-12px)] lg:min-w-[calc(25%-18px)] snap-start bg-white rounded-2xl overflow-hidden border border-[#E5E7EB] hover:shadow-lg hover:scale-105 transition-all duration-300 group flex flex-col cursor-pointer"
                        >
                            <div className="relative h-[220px] m-3 rounded-xl overflow-hidden">
                                <Image
                                    src={BLOG_IMAGES[post.slug] ?? "/images/skill-1.jpg"}
                                    alt={post.hero.title}
                                    fill
                                    className="object-cover group-hover:scale-110 transition-transform duration-700"
                                />
                                <div className="absolute top-3 left-3 bg-white px-3 py-1.5 rounded-lg shadow-sm text-[11px] font-bold text-[#1A1A1A]">
                                    {post.hero.category}
                                </div>
                            </div>

                            <div className="p-6 pt-2 text-left flex flex-col flex-grow">
                                <h3 className="text-[#002147] text-lg font-bold mb-3 leading-snug group-hover:text-[#1B61FF] transition-colors line-clamp-2">
                                    {post.hero.title}
                                </h3>
                                <p className="text-[#666666] text-[18px] leading-relaxed mb-4 line-clamp-3">
                                    {getExcerpt(post.content)}
                                </p>
                                <span className="mt-auto inline-flex items-center gap-2 font-bold text-[#1A1A1A] text-[14px] group-hover:text-[#1B61FF] transition-all">
                                    Read More <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                                </span>
                            </div>
                        </Link>
                    ))}
                </div>

                {/* Pagination Dots */}
                <div className="flex justify-center gap-2.5 mb-8">
                    {featuredBlogs.map((_, i) => (
                        <button
                            key={i}
                            type="button"
                            onClick={() => scrollTo(i)}
                            className="relative p-3 cursor-pointer group" // Invisible padding wrapper + group target
                            aria-label={`Go to slide ${i + 1}`}
                            aria-current={activeIndex === i ? "true" : "false"} // Dynamic screen-reader state
                            >
                            {/* This is your original visual indicator element */}
                            <div
                                className={`h-2.5 rounded-full transition-all duration-300 ${
                                activeIndex === i 
                                    ? "w-8 bg-[#1B61FF]" 
                                    : "w-2.5 bg-[#E5E7EB] group-hover:bg-gray-300" // Handled hover via group-hover
                                }`}
                            />
                        </button>
                    ))}
                </div>

                {/* View All Articles CTA */}
                <div className="flex justify-center mb-12">
                    <Link
                        href="/blog"
                        className="inline-flex items-center gap-2 text-sm font-bold text-[#1B61FF] hover:underline"
                    >
                        View All Articles → 
                    </Link>
                </div>

                {/* Info Sections Added Below */}
                <div className="mt-20 space-y-16 text-left">
                    {/* About Seasoft Classes */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="space-y-8"
                    >
                        <h2 className="text-[#1A1A1A] text-2xl md:text-3xl font-bold">
                            About Seasoft Classes
                        </h2>
                        <div className="space-y-6 text-[#4B5563] text-[18px] lg:text-[18px] leading-relaxed max-w-none">
                            <p>
                                Seasoft Classes is a trusted leader in professional IT training, delivering world-class learning experiences powered by modern infrastructure and cutting-edge technology. We specialize in providing high-quality, cost-effective online training programs designed to meet the evolving needs of students, professionals, and enterprises worldwide.
                            </p>
                            <p>
                                With deep domain expertise and strong industry insight, our leadership team and training consultants bring real-world knowledge into every course. We offer comprehensive training across a wide range of IT disciplines, including Oracle, PeopleSoft, Data Warehousing, SAP, Networking, System Administration, and other in-demand technologies.
                            </p>
                            <p>
                                Seasoft Classes is supported by a global network of 1,000+ experienced instructors and consultants, each bringing 10–12 years of industry experience and 6–8 years of teaching expertise. Our instructors deliver supervised, hands-on training that bridges the gap between theory and real-world application. Driven by a diverse, skilled, and motivated talent pool, we work cohesively across geographies to provide consistent, high-quality training and career guidance. Our mission is to empower learners with practical skills, industry readiness, and confidence to succeed in today&apos;s competitive technology landscape.
                            </p>
                        </div>
                    </motion.div>

                    {/* What we offer? */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="space-y-6"
                    >
                        <h2 className="text-[#1A1A1A] text-2xl md:text-3xl font-bold">
                            What we offer?
                        </h2>
                        <div className="space-y-4 text-[#4B5563] text-[18px] lg:text-[18px] leading-relaxed max-w-none">
                            <p>
                                Whether you need to staff at a single facility or support a multi-location operation with uniquely skilled people – We have technical staffing services to help you meet your goals.
                            </p>
                            <p>
                                Our specialized recruiters understand the unique skills and requirements of the Information Technology market. They have large pool of their own resource and also broad connection throughout the industry and can quickly locate the best talent in the field.
                            </p>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}

